import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/fetchWithAuth';
import { logger, logUserAction } from '@/lib/logger';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';

interface CallStats {
  total: number;
  completed: number;
  pending: number;
  declined: number;
  voicemail: number;
  completionRate: number;
  avgNpsScore: number;
}

interface NPSCall {
  id: string;
  patientName: string;
  phoneNumber: string;
  locationId: string;
  callStatus: 'Pending' | 'In Progress' | 'Completed' | 'Declined' | 'Voicemail' | 'Failed';
  score?: number;
  createdAt: string;
  updatedAt: string;
  callDuration?: number;
}

export default function NPSCallTracker() {
  const [refreshInterval, setRefreshInterval] = useState<number>(30000); // 30 seconds
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const { selectedLocationId } = useLocation();
  const { currentOrganizationId } = useOrganization();

  // Fetch locations
  const { data: locations } = useQuery<any[]>({
    queryKey: ['/api/locations', currentOrganizationId],
    queryFn: () => {
      const orgParam = currentOrganizationId ? `?organizationId=${encodeURIComponent(currentOrganizationId)}` : '';
      return apiRequest<any[]>(`/api/locations${orgParam}`);
    },
  });

  // Fetch NPS calls with auto-refresh
  const { data: allCalls, isLoading, refetch } = useQuery<NPSCall[]>({
    queryKey: ['/api/nps/patient-calls', currentOrganizationId],
    queryFn: async () => {
      const orgParam = currentOrganizationId ? `?organizationId=${encodeURIComponent(currentOrganizationId)}` : '';
      const response = await apiRequest<NPSCall[]>(`/api/nps/patient-calls${orgParam}`);
      logger.info(`Fetched ${response?.length || 0} NPS calls`, { selectedLocationId }, 'NPS_TRACKER');
      return response || [];
    },
    refetchInterval: refreshInterval,
  });

  // Filter calls based on selected location
  const calls = React.useMemo(() => {
    if (!allCalls) {
      return [];
    }
    if (!selectedLocationId || selectedLocationId === 'all') {
      return allCalls;
    }
    return allCalls.filter(call => call.locationId === selectedLocationId);
  }, [allCalls, selectedLocationId]);

  // Calculate statistics
  const stats: CallStats = React.useMemo(() => {
    if (!calls || calls.length === 0) {
      return {
        total: 0,
        completed: 0,
        pending: 0,
        declined: 0,
        voicemail: 0,
        completionRate: 0,
        avgNpsScore: 0,
      };
    }

    const total = calls.length;
    const completed = calls.filter(call => call.callStatus === 'Completed').length;
    const pending = calls.filter(call => call.callStatus === 'Pending' || call.callStatus === 'In Progress').length;
    const declined = calls.filter(call => call.callStatus === 'Declined').length;
    const voicemail = calls.filter(call => call.callStatus === 'Voicemail').length;
    
    const completionRate = total > 0 ? (completed / total) * 100 : 0;
    
    const completedWithScores = calls.filter(call => call.callStatus === 'Completed' && call.score !== null);
    const avgNpsScore = completedWithScores.length > 0 
      ? completedWithScores.reduce((sum, call) => sum + (call.score || 0), 0) / completedWithScores.length 
      : 0;

    return {
      total,
      completed,
      pending,
      declined,
      voicemail,
      completionRate,
      avgNpsScore,
    };
  }, [calls]);

  // Auto-refresh effect
  useEffect(() => {
    const interval = setInterval(() => {
      setLastRefresh(new Date());
      refetch();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [refreshInterval, refetch]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed': return 'bg-green-500';
      case 'In Progress': return 'bg-blue-500';
      case 'Pending': return 'bg-yellow-500';
      case 'Declined': return 'bg-red-500';
      case 'Voicemail': return 'bg-orange-500';
      case 'Failed': return 'bg-gray-500';
      default: return 'bg-gray-400';
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Completed': return 'default';
      case 'In Progress': return 'secondary';
      case 'Pending': return 'outline';
      case 'Declined': return 'destructive';
      case 'Voicemail': return 'secondary';
      default: return 'outline';
    }
  };

  const handleManualRefresh = () => {
    logUserAction('manual_refresh_nps_calls', { selectedLocationId });
    setLastRefresh(new Date());
    refetch();
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>NPS Call Tracker</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="text-muted-foreground">Loading call data...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>🎯 NPS Call Tracker - Real-Time</CardTitle>
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                Location filter controlled by header selector
              </div>
              <Button onClick={handleManualRefresh} variant="outline" size="sm">
                🔄 Refresh
              </Button>
            </div>
          </div>
          <div className="text-sm text-muted-foreground">
            Last updated: {lastRefresh.toLocaleTimeString()} • Auto-refresh every {refreshInterval / 1000}s
          </div>
        </CardHeader>
      </Card>

      {/* Statistics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-muted-foreground">Total Calls</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
            <div className="text-sm text-muted-foreground">Completed</div>
            <Progress value={stats.completionRate} className="mt-2" />
            <div className="text-xs text-muted-foreground mt-1">
              {stats.completionRate.toFixed(1)}% completion rate
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
            <div className="text-sm text-muted-foreground">Pending</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">
              {stats.avgNpsScore > 0 ? stats.avgNpsScore.toFixed(1) : 'N/A'}
            </div>
            <div className="text-sm text-muted-foreground">Avg NPS Score</div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Calls */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Calls</CardTitle>
        </CardHeader>
        <CardContent>
          {calls && calls.length > 0 ? (
            <div className="space-y-3">
              {calls.slice(0, 10).map((call) => (
                <div key={call.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(call.callStatus)}`} />
                    <div>
                      <div className="font-medium">{call.patientName}</div>
                      <div className="text-sm text-muted-foreground">{call.phoneNumber}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {call.score !== null && call.score !== undefined && (
                      <div className="text-sm font-medium">
                        Score: {call.score}/10
                      </div>
                    )}
                    <Badge variant={getStatusBadgeVariant(call.callStatus)}>
                      {call.callStatus}
                    </Badge>
                    <div className="text-xs text-muted-foreground">
                      {new Date(call.updatedAt).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-8">
              No calls found for the selected location
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 